import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import PaperDraggable from "../../../../../components/PaperDraggable";
import { FormProvider, useForm } from "react-hook-form";
import moment from "moment";
import { formatarMonetárioParaNúmero } from "../../../../../helpers/masks";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import StepperIncluirPagamento from "./Stepper";
import { TParcelas } from "./Stepper/Step02/InfoStep02";
import { useModalDetalheProcesso } from "../../../../../components/ModalContexts/ModalDetalheProcessoContext";

export default function DialogIncluirPagamento({
  open,
  handleClose,
}: DialogIncluirRecebimentoProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { openModal } = useModalDetalheProcesso();

  //Querys e mutations
  const { mutate: cadastrarTitulo } = useMutation({
    mutationKey: "cadastrar-titulo",
    mutationFn: (inputs: any) =>
      axiosClient.post("/financeiro/contas-pagar/cadastrar-titulo-pagar", {
        ...inputs,
        forma_pagamento: null,
      }),
    onError: (error) => {
      console.error(error);
      enqueueSnackbar("Erro ao cadastrar título!", { variant: "error" });
    },
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries([
        "table-data-grid-financeiro/contas-pagar/pesquisar-titulos-pagar",
      ]);
      redirectToTitulo(data);
      handleClose();
      enqueueSnackbar("Título cadastrado com sucesso!", { variant: "success" });
    },
    onSettled: () => setValue("isLoadingIncluirTitulo", false),
  });

  const { mutate: solicitarCriacaoTitulo } = useMutation({
    mutationKey: "solicitar-criacao-titulo",
    mutationFn: (inputs: any) =>
      axiosClient.post(
        "/financeiro/contas-pagar/solicitar-criacao",
        inputs,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      ),
    onError: (error) => {
      console.error(error);
      enqueueSnackbar("Erro ao solicitar criação de título!", {
        variant: "error",
      });
    },
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries([
        "table-data-grid-financeiro/contas-pagar/pesquisar-titulos-pagar",
      ]);
      handleClose();
      enqueueSnackbar(`Processo #${data} criado com sucesso!`, {
        variant: "success",
      });
      openModal(data);
      navigate(`/tarefas?detalhe-processo=${data}`);
    },
    onSettled: () => setValue("isLoadingIncluirTitulo", false),
  });

  //Funções manipuladoras
  const handleNextStep = () => {
    const stepper = getValues("stepper");
    setValue("stepper", stepper + 1);
  };

  const handleBackStep = () => {
    const stepper = getValues("stepper");
    setValue("stepper", stepper - 1);
  };

  const redirectToTitulo = (data: string) => {
    const match = data.match(/^([A-Z0-9]+)-(\d+)\/(\d+)$/);

    if (match) {
      const [, tipo, numero] = match;
      navigate(
        `/financeiro/contas-pagar/listar-todas?tipo_documento_pesquisar=${tipo}&numero_documento_pesquisar=${numero}`
      );
    }
  };

  const onSubmit = (data: any) => {
    setValue("isLoadingIncluirTitulo", true);
    let params: any = {
      //STEP01
      tipo_publico: data.step01.publico_dado.tipo_publico,
      codigo_publico: data.step01.publico_dado.codigo_publico,
      tipo_documento_id: data.step01.tipo_documento.id,
      numero_documento: data.step01.numero_documento,
      serie_documento: data.step01.serie,
      //STEP02
      data_emissao: moment(data.step02.data_emissao).format("YYYY-MM-DD"),
      ...(data.step02.is_parcelado === "false" && {
        data_vencimento: moment(data.step02.data_vencimento).format("YYYY-MM-DD"),
      }),
      valor_total: formatarMonetárioParaNúmero(data.step02.valor_total),
      is_parcelado: data.step02.is_parcelado,
      ...(data.step02.is_parcelado === "true" && {
        parcelas: data.step02.parcelas.map((valor: TParcelas) => {
          return {
            valor: formatarMonetárioParaNúmero(valor.valor_parcela),
            numero_parcela:
              valor.numero_parcela < 10
                ? valor.numero_parcela
                : valor.numero_parcela,
            data_vencimento: moment(valor.data_vencimento).format("YYYY-MM-DD"),
          };
        }),
      }),
    };
    if (data.step_aprovacao.option === "abrir_solicitacao") {
      const formData = new FormData();
      formData.append("justificativa", data.step_aprovacao.justificativa);
      data.step_aprovacao.anexos.forEach((anexo: File, index: number) => {
        formData.append(`anexos[${index}]`, anexo);
      });

      // Adiciona o restante dos parâmetros ao formData
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((item, index) => {
            formData.append(`${key}[${index}]`, JSON.stringify(item));
          });
        } else {
          formData.append(key, String(value));
        }
      });

      solicitarCriacaoTitulo(formData);
    }
    if (data.step_aprovacao.option === "criar_titulo") {
      params = {
        ...params,
        //STEP03
        agencia: data.step03.agencia,
        agencia_digito: data.step03.agencia_digito,
        ...(data.step03.conta_bancaria && {
            conta: data.step03.conta_bancaria.conta,
            conta_digito: data.step03.conta_bancaria.conta_digito,
            agencia: data.step03.conta_bancaria.agencia,
            agencia_digito: data.step03.conta_bancaria.agencia_digito,
            banco_pagamento_id: data.step03.conta_bancaria.banco.id,
            nome_conta: data.step03.conta_bancaria.nome,
            cpf_cnpj_conta: data.step03.conta_bancaria.cpf_cnpj_conta,
            tipo_cnpj_conta: data.step03.conta_bancaria.tipo_cnpj_conta,
            tipo_conta_bancaria: data.step03.conta_bancaria.tipo_conta_bancaria,
        }),
        local_cobranca_id: data.step03.local_cobranca.codigo,
        conta_devedora_codigo: data.step03.conta_devedora.codigo_contabil,
        ...(data.step03.conta_devedora.centro_custo && {
          conta_devedora_custo: data.step03.conta_devedora.centro_custo.id,
        }),
        conta_credora_codigo: data.step03.conta_credora.codigo_contabil,
        ...(data.step03.conta_credora.centro_custo && {
          conta_credora_custo: data.outrasInfos.conta_credora.centro_custo.id,
        }),
        departamento_id: data.step03.departamento.id,
        observacao: data.step03.observacao,
        codigo_natureza_rendimento: data.step03.natureza_rendimento.codigo,
        historico_id: data.step03.historico.id,
        historico_descricao: data.step03.historico.descricao,
        ...(data.step04.add_rateio === "true" && {
          rateios: data.step04.rateios.map((rateio: any) => ({
            conta_devedora_codigo: rateio.contaDevedora.codigo_contabil,
            conta_devedora_custo:
              rateio.contaDevedora?.centro_custo?.id || null,
            valor: formatarMonetárioParaNúmero(rateio.valor),
          })),
        }),
        ...(data.step05.add_retencoes === "true" && {
          retencoes: data.step05.retencoes
            .filter((retencao: any) => retencao.checked)
            .map((retencao: any) => ({
              id: retencao.retencao.id,
              valor: formatarMonetárioParaNúmero(retencao.valor),
              data_vencimento: moment(retencao.vencimento).format("YYYY-MM-DD"),
            })),
        }),
      };
      cadastrarTitulo(params);
    }
  };
  //

  //valores importantes

  const defaultValuesForm = {
    stepper: 0,
    isLoadingIncluirTitulo: false,
    step01: {
      publico_dado: "",
      tipo_documento: "",
      numero_documento: "",
      serie: "",
    },
    step02: {
      data_vencimento: moment(),
      data_emissao: moment(),
      valor_total: "",
      is_parcelado: "false",
      parcelas: [
        {
          valor_parcela: "",
          numero_parcela: 1,
          data_vencimento: moment(),
        },
      ],
    },
    step_aprovacao: {
      option: "abrir_solicitacao",
      justificativa: "",
      anexos: [],
    },
    step03: {
      banco_pagamento_id: null,
      agencia: "",
      agencia_digito: "",
      conta: "",
      conta_digito: "",
      local_cobranca: "",
      conta_devedora: "",
      conta_credora: "",
      departamento: "",
      historico: "",
      natureza_rendimento: "",
      observacao: "",
    },
    step04: {
      add_rateio: "false",
      rateios: [],
    },
    step05: {
      add_retencoes: "false",
      retencoes: [],
    },
    funcoesAux: {
      onNext: handleNextStep,
      onBack: handleBackStep,
      onCloseModal: handleClose,
      onSubmit: onSubmit,
    },
  };

  const methods = useForm({ defaultValues: defaultValuesForm });
  const { getValues, setValue } = methods;

  //

  return (
    <FormProvider {...methods}>
      <Dialog
        open={open}
        PaperComponent={PaperDraggable}
        aria-labelledby="draggable-dialog-title"
        fullWidth
        maxWidth={"md"}
        scroll="body"
      >
        <DialogTitle style={{ cursor: "move" }} id="draggable-dialog-title">
          <Typography variant="h6">Incluir título a pagar</Typography>
        </DialogTitle>

        <DialogContent>
          <StepperIncluirPagamento />
        </DialogContent>

        <DialogActions>
          <Button variant="text" onClick={handleClose}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}

interface DialogIncluirRecebimentoProps {
  open: boolean;
  handleClose: () => void;
}
